import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  GettingStartedApi,
  DownloadUrlResponse,
  AudioUrlResponse,
  VideoUrlResponse,
} from './types';

/**
 * API reference for Getting Started plugin
 */
export const gettingStartedApiRef: ApiRef<GettingStartedApi> =
  createApiRef<GettingStartedApi>({
    id: 'plugin.getting-started.service',
  });

/**
 * Client for communicating with the getting-started backend plugin
 * Uses discoveryApi for direct backend plugin communication (no proxy needed)
 */
export class GettingStartedClient implements GettingStartedApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /**
   * Request a pre-signed download URL for team-specific configuration files
   * Implements 10-second timeout as specified in PRD
   */
  async getDownloadUrl(
    team: string,
    maintainerType: string,
  ): Promise<DownloadUrlResponse> {
    // Resolve backend plugin URL using discoveryApi (direct communication, no proxy)
    const baseUrl = await this.discoveryApi.getBaseUrl('getting-started');
    const url = `${baseUrl}/download`;

    // Create AbortController for 10-second timeout
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      timeoutId = setTimeout(() => controller.abort(), 10000); // 10 seconds
    } catch {
      // If setTimeout fails, continue without timeout
    }

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ team, maintainerType }),
        signal: controller.signal,
      });

      // Clear timeout on successful response
      if (timeoutId !== undefined) {
        try {
          clearTimeout(timeoutId);
        } catch {
          // Ignore clearTimeout errors in test environments
        }
      }

      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }

      return await response.json();
    } catch (error) {
      // Clear timeout on error
      if (timeoutId !== undefined) {
        try {
          clearTimeout(timeoutId);
        } catch {
          // Ignore clearTimeout errors in test environments
        }
      }

      // Handle abort/timeout error
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }

      throw error;
    }
  }

  /**
   * Request a pre-signed URL for audio file
   * No timeout implementation needed for audio as it's a simple GET request
   */
  async getAudioUrl(audioId: string): Promise<AudioUrlResponse> {
    // Resolve backend plugin URL using discoveryApi
    const baseUrl = await this.discoveryApi.getBaseUrl('getting-started');
    const url = `${baseUrl}/audio/${encodeURIComponent(audioId)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return await response.json();
  }

  /**
   * Request a pre-signed URL for video file
   * No timeout implementation needed for video as it's a simple GET request
   */
  async getVideoUrl(videoId: string): Promise<VideoUrlResponse> {
    // Resolve backend plugin URL using discoveryApi
    const baseUrl = await this.discoveryApi.getBaseUrl('getting-started');
    const url = `${baseUrl}/video/${encodeURIComponent(videoId)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return await response.json();
  }
}
